import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

interface MarkdownMessageProps {
  content: string;
  onLinkClick: (url: string, linkText: string) => void;
}

export function MarkdownMessage({
  content,
  onLinkClick,
}: MarkdownMessageProps) {
  return (
    <div className="prose prose-sm max-w-none wrap-break-word text-base leading-[1.21] text-black">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          p: ({ node, ...props }) => (
            <p
              {...props}
              className="mb-4 whitespace-pre-wrap leading-[1.21] last:mb-0"
            />
          ),
          a: ({ node, children, ...props }) => (
            <a
              {...props}
              className="text-blue-600 underline hover:text-blue-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 focus-visible:ring-offset-white rounded"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => {
                const href = props.href || "";
                const linkText =
                  typeof children === "string"
                    ? children
                    : Array.isArray(children)
                      ? children.join("")
                      : "link";

                onLinkClick(href, linkText);
              }}
            >
              {children}
              <span className="sr-only"> opens in new tab</span>
            </a>
          ),
          code: ({ node, className, children, ...props }) => {
            const isInline = !className;
            return isInline ? (
              <code
                {...props}
                className="rounded bg-gray-100 px-1.5 py-0.5 font-mono text-sm text-gray-800"
              >
                {children}
              </code>
            ) : (
              <code
                {...props}
                className={`${className} block rounded bg-gray-100 p-3 font-mono text-sm text-gray-800`}
              >
                {children}
              </code>
            );
          },
          pre: ({ node, ...props }) => (
            <pre
              {...props}
              className="mb-4 overflow-x-auto rounded bg-gray-100 p-3 last:mb-0"
            />
          ),
          ul: ({ node, ...props }) => (
            <ul {...props} className="mb-4 ml-6 list-disc last:mb-0" />
          ),
          ol: ({ node, ...props }) => (
            <ol {...props} className="mb-4 ml-6 list-decimal last:mb-0" />
          ),
          li: ({ node, ...props }) => <li {...props} className="mb-1" />,
          h1: ({ node, ...props }) => (
            <h1 {...props} className="mb-4 text-2xl font-bold last:mb-0" />
          ),
          h2: ({ node, ...props }) => (
            <h2 {...props} className="mb-3 text-xl font-bold last:mb-0" />
          ),
          h3: ({ node, ...props }) => (
            <h3 {...props} className="mb-3 text-lg font-bold last:mb-0" />
          ),
          h4: ({ node, ...props }) => (
            <h4 {...props} className="mb-2 text-base font-bold last:mb-0" />
          ),
          h5: ({ node, ...props }) => (
            <h5 {...props} className="mb-2 text-sm font-bold last:mb-0" />
          ),
          h6: ({ node, ...props }) => (
            <h6 {...props} className="mb-2 text-xs font-bold last:mb-0" />
          ),
          blockquote: ({ node, ...props }) => (
            <blockquote
              {...props}
              className="mb-4 border-l-4 border-gray-300 pl-4 italic last:mb-0"
            />
          ),
          table: ({ node, ...props }) => (
            <div className="mb-4 overflow-x-auto last:mb-0">
              <table
                {...props}
                className="min-w-full border-collapse border border-gray-300"
              />
            </div>
          ),
          thead: ({ node, ...props }) => (
            <thead {...props} className="bg-gray-100" />
          ),
          th: ({ node, ...props }) => (
            <th
              {...props}
              className="border border-gray-300 px-4 py-2 text-left font-semibold"
            />
          ),
          td: ({ node, ...props }) => (
            <td {...props} className="border border-gray-300 px-4 py-2" />
          ),
          hr: ({ node, ...props }) => (
            <hr {...props} className="my-4 border-gray-300" />
          ),
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
}
